<template>
    <div class="event__edit_bookings">
        <component-title :size="'medium'">{{ $t('ebs.events.edit.labels.bookingLimits') }}</component-title>
        <form-number
            :label="$t('ebs.events.edit.labels.minimumBookingLimits')"
            :name="'minimum-booking-limit'"
            :required="true"
            :v-validate="'required|min_value:0'"
            :data-vv-as="$t('forms.dataVvAs.minimumBookingLimits')"
            v-model="eventBookingMin"
        />
        <form-notification
            :trigger-event="'MinimumBookingsReachedEvent'"
            :name="'Minimum bookings are reached'"
        />
        <form-radio-horizontal
            :label="$t('ebs.events.edit.labels.setLimitTo')"
            :items="bookingLimitItems"
            :name="'booking-limit-to'"
            :data-vv-as="$t('forms.dataVvAs.setLimitTo')"
            :required="true"
            v-model="eventBookingType"
        >
            <transition name="fade" mode="out-in">
                <template v-if="eventBookingType === 'roomBased'">{{ $t('ebs.events.edit.title.theBookingLimitText') }}
                </template>
            </transition>
        </form-radio-horizontal>
        <form-number
            v-if="eventBookingType === 'fixed'"
            :label="$t('ebs.events.edit.labels.maximumBookingLimits')"
            :name="'maximum-booking-limit'"
            :data-vv-as="$t('forms.dataVvAs.maximumBookingLimits')"
            :required="true"
            :v-validate="`required|min_value:${eventBookingMin}`"
            v-model="eventBookingMax"
        />
        <form-notification
            :trigger-event="'MaximumBookingsReachedEvent'"
            :name="'Maximum bookings are reached'"
        />
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.acceptingBookings') }}</component-title>
        <form-number-radio-horizontal
            :label="$t('ebs.events.edit.labels.accept')"
            :name="'bookings-to'"
            :after="$t('ebs.events.edit.title.before')"
            :items="[
                {
                    name: this.$t('ebs.events.edit.name.hours'),
                    value: 'hour'
                },
                {
                    name: this.$t('ebs.events.edit.name.days'),
                    value: 'day'
                }
            ]"
            :required="true"
            :data-vv-as="$t('forms.dataVvAs.acceptBookings')"
            :value-number="eventAcceptancePeriod"
            :value-radio="eventAcceptancePeriodType"
            :v-validate="'required|min_value:0'"
            @emit-value-number="eventUpdateAcceptancePeriod"
            @emit-value-radio="eventUpdateAcceptancePeriodType"
        />
        <form-checkbox
            v-if="false"
            :label="$t('ebs.events.edit.labels.allowBooking')"
            :name="'accepting-bookings'"
            v-model="eventAllowNoEnrolments">
        </form-checkbox>
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.bookingForm') }}</component-title>
        <form-checkbox
            :label="$t('ebs.events.edit.labels.bookingForm')"
            :name="'booking-form'"
            v-model="eventBookingFormRequired"
        >{{ $t('ebs.events.edit.title.learnersMust') }}
        </form-checkbox>
        <transition name="fade" mode="out-in">
            <template
                v-if="eventBookingFormRequired"
            >
                <form-customform
                    :title="$t('ebs.events.edit.title.ConfigureBookingForm')"
                    :items="eventBookingFormFields"
                    :previewSubmitText="$t('ebs.events.edit.buttons.bookOnto')"
                    @emit-notify-add="eventAddBookingFormField"
                    @emit-notify-edit="eventEditBookingFormField"
                    @emit-notify-remove="eventRemoveBookingFormField"
                    @emit-move="eventMoveBookingFormField"
                    :name="'event-configure-booking-form'"/>
                />
            </template>
        </transition>
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.sessionReminder') }}</component-title>
        <form-checkbox
            :label="$t('ebs.events.edit.labels.sessionReminderCheck')"
            :name="'event-reminder'"
            v-model="eventBookingReminders"
        >
        </form-checkbox>
        <form-number-radio-horizontal
            :label="''"
            :name="'reminder-checkbox-to'"
            :after="$t('ebs.events.edit.labels.sessionReminderAfter')"
            :items="[
                {
                    name: $t('ebs.events.edit.name.hours'),
                    value: 'hour'
                },
                {
                    name: $t('ebs.events.edit.name.days'),
                    value: 'day'
                }
            ]"
            :disabled="!eventBookingReminders"
            :data-vv-as="$t('forms.dataVvAs.acceptBookings')"
            :v-validate="'required|min_value:0'"
            :value-number="eventBookingReminderInterval"
            :value-radio="eventBookingReminderIntervalType"
            @emit-value-number="eventUpdateReminderInterval"
            @emit-value-radio="eventUpdateReminderIntervalType"
        />
        <form-notification
            :disabled="!eventBookingReminders"
            :identifier="'session.reminder.learner'"
            :name="$t('ebs.events.edit.name.sessionReminderNotificationLearner')"
            :label="$t('ebs.events.edit.notification.bookingCapacity')"
            id="'form-notification-event-reminder-learner'"
        />
        <form-notification
            :disabled="!eventBookingReminders"
            :identifier="'session.reminder.trainer'"
            :name="$t('ebs.events.edit.name.sessionReminderNotificationTrainer')"
            :label="$t('ebs.events.edit.notification.bookingCapacity')"
            id="'form-notification-event-reminder-trainer'"
        />
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="eventReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            :class="'pop__notify--validation'"
            ref="popnotifyvalidation"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="eventSubmit"
        />
    </div>
</template>

<script>
import FormNumber from '@/components/default/forms/FormNumber'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormNotification from '@/components/default/forms/FormNotification'
import FormNumberRadioHorizontal from '@/components/default/forms/FormNumberRadioHorizontal'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormSubmit from '@/components/default/forms/FormSubmit'
import FormCustomform from '@/components/default/forms/FormCustomform'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import { watchContentChangeEmit } from '@/helpers/mixins'

export default {
  name: 'event-edit-bookings',
  mixins: [watchContentChangeEmit],
  components: {
    ComponentTitle,
    PopNotify,
    FormCustomform,
    FormSubmit,
    FormCheckbox,
    FormNumberRadioHorizontal,
    FormNotification,
    FormRadioHorizontal,
    FormNumber
  },
  computed: {
    eventName: {
      get () {
        return this.$store.getters['getEventName']
      },
      set (value) {
        this.$store.dispatch('setEventName', value)
      }
    },
    eventAllowNoEnrolments: {
      get () {
        return this.$store.getters['getEventBookingAllowNoEnrolments']
      },
      set (value) {
        this.$store.dispatch('setEventBookingAllowNoEnrolments', value)
      }
    },
    eventBookingMin: {
      get () {
        return this.$store.getters['getEventBookingMinLimit']
      },
      set (value) {
        this.$store.dispatch('setEventBookingMinLimit', value)
      }
    },
    eventBookingMax: {
      get () {
        return this.$store.getters['getEventBookingMaxLimit']
      },
      set (value) {
        this.$store.dispatch('setEventBookingMaxLimit', value)
      }
    },
    eventBookingType: {
      get () {
        return this.$store.getters['getEventBookingLimitType']
      },
      set (value) {
        this.$store.dispatch('setEventBookingLimitType', value)
      }
    },
    eventBookingReminders: {
      get () {
        return this.$store.getters['getEventBookingSendReminders']
      },
      set (value) {
        this.$store.dispatch('setEventBookingSendReminders', value)
      }
    },
    eventBookingReminderInterval: {
      get () {
        return this.$store.getters['getEventBookingReminderInterval']
      }
    },
    eventBookingReminderIntervalType: {
      get () {
        return this.$store.getters['getEventBookingReminderIntervalType']
      }
    },
    eventAcceptancePeriod: {
      get () {
        return this.$store.getters['getEventBookingAcceptancePeriod']
      }
    },
    eventAcceptancePeriodType: {
      get () {
        return this.$store.getters['getEventBookingAcceptancePeriodType']
      }
    },
    eventBookingFormFields: {
      get () {
        return this.$store.getters['getEventBookingFormFields']
      }
    },
    eventBookingFormRequired: {
      get () {
        return this.$store.getters['getEventBookingFormRequired']
      },
      set (value) {
        this.$store.dispatch('setEventBookingFormRequired', value)
      }
    },
    eventOccurrenceSessionType: {
      get () {
        return this.$store.getters['getEventOccurrenceSessionType']
      }
    },
    bookingLimitItems () {
      let itemsArr = []

      if (this.eventOccurrenceSessionType === 'face_to_face') {
        itemsArr.push({
          name: this.$t('ebs.sessions.edit.name.roomCapacity'),
          value: 'roomBased'
        })
      } else {
        itemsArr.push({
          name: 'Unlimited',
          value: 'unlimited'
        })
      }
      itemsArr.push({
        name: this.$t('ebs.sessions.edit.name.specificValue'),
        value: 'fixed'
      })

      return itemsArr
    },
    watchContent () {
      return this.eventName + this.eventAllowNoEnrolments + this.eventBookingMin +
      this.eventBookingMax + this.eventBookingType + this.eventAcceptancePeriod +
      this.eventAcceptancePeriodType + this.eventBookingFormFields + this.eventBookingFormRequired
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    },
    isOldLMSToken () {
      return this.$store.getters.getAuthUserIsSecondStageTokenOldLMS
    }
  },
  methods: {
    validateBeforeRouteLeave: function (to) {
      if (to.name.includes('ebs-event-edit') || to.name.includes('ebs-event-add')) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$parent.validated = true
            this.$router.push({ path: to.path })
          } else {
            this.$refs.popnotifyvalidation.openNotify()
          }
        })
      } else {
        this.$parent.validated = true
        this.$router.push({ path: to.path })
      }
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('event-submit')
    },
    beforeEventSubmit () {
      if (this.eventHasOccurrences) {
        this.openNotifyNotAffectSessions()
      } else {
        this.eventSubmit()
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    checkEventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.$refs.popnotifyhasdraftsessions.openNotify()
      }
    },
    //
    eventUpdateReminderInterval: function () {
      this.$store.dispatch('setEventBookingReminderInterval', event.target.value)
    },
    eventUpdateReminderIntervalType: function () {
      this.$store.dispatch('setEventBookingReminderIntervalType', event.target.value)
    },
    eventUpdateAcceptancePeriod: function () {
      this.$store.dispatch('setEventBookingAcceptancePeriod', event.target.value)
    },
    eventUpdateAcceptancePeriodType: function () {
      this.$store.dispatch('setEventBookingAcceptancePeriodType', event.target.value)
    },
    eventAddBookingFormField: function (data) {
      this.$store.dispatch('setEventBookingFormField', data)
    },
    eventEditBookingFormField: function (data, index) {
      this.$store.dispatch('updateEventBookingFormField', { data: data, index: index })
    },
    eventRemoveBookingFormField: function (data) {
      this.$store.dispatch('removeEventBookingFormField', data)
    },
    eventMoveBookingFormField: function (data) {
      this.$store.dispatch('moveEventBookingFormField', data)
    },
    //
    eventReset: function () {
      this.$store.dispatch('resetEvent')
    },
    eventSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-event-add')) {
            this.$store.dispatch('createEvent')
          } else if (this.$route.name.includes('ebs-event-edit')) {
            this.$store.dispatch('updateEvent')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  watch: {
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
      }
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>
